import React, { useState } from "react";
import * as XLSX from "xlsx"; 
import { saveExcelData, fetchFailedInvoices } from "./Transactions";
import { CallStates } from "../../../components/SharedComponents/AppStates/Utils";

const FailedInvoiceStep = ({
  api,
  failedRows,
  setFailedRows,
  setCurrentStep,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isActionSuccess, setActionSuccess] = useState(false);
  const [callState, setCallState] = useState(CallStates.INITIAL);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const visibleRows = failedRows.filter((row) => row.Status !== "New");

  const statusOptions = [
    "All",
    ...new Set(visibleRows.map((row) => row.Status).filter(Boolean)),
  ];

  const filteredRows = visibleRows.filter((row) => {
    if (statusFilter !== "All" && row.Status !== statusFilter) {
      return false;
    }
    if (row.InsertedDate) {
      const rowDate = new Date(row.InsertedDate);
      if (dateFrom && rowDate < new Date(dateFrom)) {
        return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (rowDate > to) {
          return false;
        }
      }
    }
    return true;
  });

  const handleClearFilters = () => {
    setStatusFilter("All");
    setDateFrom("");
    setDateTo("");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }
    const parsed = new Date(dateValue);
    return isNaN(parsed) ? "" : parsed.toLocaleDateString();
  };

  const formatAmount = (amount) => `£${Number(amount || 0).toFixed(2)}`;

  const statusBadgeClass = (status) => {
    if (status === "Unable to process invoice") {
      return "invoice-status-badge invoice-status-danger";
    }
    if (status === "Duplicate transaction already exists") {
      return "invoice-status-badge invoice-status-warning";
    }
    return "invoice-status-badge invoice-status-neutral";
  };

  const handleCheckboxChange = (InvoiceListBulkPaymentID) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(InvoiceListBulkPaymentID)) {
        return prevSelectedRows.filter((id) => id !== InvoiceListBulkPaymentID);
      } else {
        return [...prevSelectedRows, InvoiceListBulkPaymentID];
      }
    });
    setErrorMessage(null);
  };

  const handleSelectAllChange = () => {
    const filteredIds = filteredRows.map((row) => row.InvoiceListBulkPaymentID);
    const allFilteredSelected =
      filteredIds.length > 0 &&
      filteredIds.every((id) => selectedRows.includes(id));
    if (allFilteredSelected) {
      setSelectedRows((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedRows((prev) => [...new Set([...prev, ...filteredIds])]);
    }
    setErrorMessage(null);
  };

  const handleDeleteSelected = (InvoiceListBulkPaymentID) => {
    const selectedFailedRows = visibleRows.filter((row) =>
      selectedRows.includes(row.InvoiceListBulkPaymentID)
    );
    const summary = { invoicelist: selectedFailedRows, module: "Delete" };

    saveExcelData(api, summary, setCallState)
      .then(() => {
        fetchFailedInvoices(api).then((response) => {
          const failedItems = response.InvoiceListItems;
          if (failedItems.length > 0) {
            setFailedRows(failedItems);
            setActionSuccess(false);
          } else {
            setFailedRows([]);
            setActionSuccess(true);
            setErrorMessage([]);
          }
        });
      })
      .catch(() => {
        setErrorMessage("Failed to delete selected invoices.");
        setActionSuccess(false);
      });
  };

  const handleDeleteRow = (InvoiceListBulkPaymentID) => {
    const updatedSelectedRows = selectedRows.includes(InvoiceListBulkPaymentID)
      ? selectedRows.filter((id) => id !== InvoiceListBulkPaymentID)
      : [...selectedRows, InvoiceListBulkPaymentID];
    setSelectedRows(updatedSelectedRows);

    const selectedFailedRows = visibleRows.filter((row) =>
      updatedSelectedRows.includes(row.InvoiceListBulkPaymentID)
    );

    const summary = { invoicelist: selectedFailedRows, module: "Delete" };

    saveExcelData(api, summary, setCallState)
      .then(() => {
        fetchFailedInvoices(api).then((response) => {
          const failedItems = response.InvoiceListItems;
          if (failedItems.length > 0) {
            setFailedRows(failedItems);
            setActionSuccess(false);
          } else {
            setFailedRows([]);
            setActionSuccess(true);
            setErrorMessage(null);
          }
        });
      })
      .catch(() => {
        setErrorMessage("Failed to delete selected invoices.");
        setActionSuccess(false);
      });
  };

  const handleDownloadExcel = () => {
    if (filteredRows.length === 0) {
      setErrorMessage("No invoices to download.");
      return;
    }
    const worksheetData = filteredRows.map((row) => ({
      "Healthcode / Invoice No": row.InvoiceId,
      "Amount to collect": row.Amount,
      Status: row.Status,
      Date: formatDate(row.InsertedDate),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment collection");
    XLSX.writeFile(workbook, "Payment collection template.xlsx");
  };

  const isSaving = callState === CallStates.SAVING;
  const allFilteredSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedRows.includes(row.InvoiceListBulkPaymentID));

  return (
    <div className="failed-invoices-container">
      <div className="failed-invoices-header">
        <div>
          <h2>Failed Invoices</h2>
          <p className="failed-invoices-subtitle">
            Invoices that failed processing and need review or removal.
          </p>
        </div>
        <a
          href="#"
          className="invoice-table-link"
          onClick={(e) => {
            e.preventDefault();
            setCurrentStep("upload");
          }}
        >
          ← Go to Upload Page
        </a>
      </div>

      {visibleRows.length > 0 ? (
        <div>
          <div className="invoice-toolbar">
            <div className="invoice-toolbar-summary">
              <span className="invoice-toolbar-count">{filteredRows.length}</span>
              <span>
                {filteredRows.length === visibleRows.length
                  ? "failed invoices"
                  : `of ${visibleRows.length} failed invoices`}
              </span>
            </div>
            <div className="invoice-filter-bar">
              <label>
                Status
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                From
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="invoice-link-button"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="invoice-select-all-bar">
            <label className="invoice-select-all-label">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleSelectAllChange}
                disabled={isSaving || filteredRows.length === 0}
              />
              Select All
            </label>
            {selectedRows.length > 0 && (
              <span className="invoice-selected-count">
                {selectedRows.length} selected
              </span>
            )}
          </div>

          {filteredRows.length === 0 ? (
            <div className="invoice-card-empty">
              No failed invoices match the selected filters.
            </div>
          ) : (
            <div className="invoice-card-grid">
              {filteredRows.map((row) => {
                const isSelected = selectedRows.includes(
                  row.InvoiceListBulkPaymentID
                );
                return (
                  <div
                    key={row.InvoiceListBulkPaymentID}
                    className={
                      "invoice-card" +
                      (isSelected ? " invoice-card-selected" : "")
                    }
                  >
                    <input
                      type="checkbox"
                      className="invoice-card-checkbox"
                      checked={isSelected}
                      disabled={isSaving}
                      onChange={() =>
                        handleCheckboxChange(row.InvoiceListBulkPaymentID)
                      }
                    />
                    <div className="invoice-card-body">
                      <div className="invoice-card-top">
                        <span className="invoice-card-number">
                          Invoice #{row.InvoiceId}
                        </span>
                        <span className="invoice-card-amount">
                          {formatAmount(row.Amount)}
                        </span>
                      </div>
                      <div className="invoice-card-bottom">
                        <span className={statusBadgeClass(row.Status)}>
                          {row.Status}
                        </span>
                        {formatDate(row.InsertedDate) && (
                          <span className="invoice-card-date">
                            {formatDate(row.InsertedDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="invoice-card-delete"
                      type="button"
                      disabled={isSaving}
                      onClick={() =>
                        handleDeleteRow(row.InvoiceListBulkPaymentID)
                      }
                      aria-label={`Delete invoice ${row.InvoiceId}`}
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="retry-button-container">
            <button
              className="invoice-table-button invoice-table-button-secondary"
              type="button"
              disabled={isSaving || selectedRows.length === 0}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </button>
            <button
              className="invoice-table-button invoice-table-button-secondary"
              type="button"
              disabled={isSaving}
              onClick={handleDownloadExcel}
            >
              Download as Excel
            </button>
          </div>

          {isSaving && (
            <div className="invoice-upload-loading">Processing, please wait...</div>
          )}
          {isActionSuccess && (
            <div className="invoice-bulk-message-success">
              Selected invoices processed successfully!
            </div>
          )}
          {errorMessage && (
            <div className="invoice-bulk-error-message">{errorMessage}</div>
          )}
        </div>
      ) : (
        <div className="invoice-bulk-message-success">
          <p>No failed invoices to retry.</p>
        </div>
      )}
    </div>
  );
};

export default FailedInvoiceStep;
